import {Component, inject, Input, OnInit, ViewChild} from '@angular/core';
import {SearchObject} from '../../../core/definitions/search-object';
import {SearchResultSelectionsService} from '../../search-result-selections.service';
import {SearchExecutorService} from '../../search-executor.service';
import {SearchHandlerService} from '../../search-handler.service';
import {VirtualScrollSearchDataSourceList} from '../../virtual-scroll-search-data-source-list';
import {CdkVirtualScrollViewport} from '@angular/cdk/scrolling';
import {SearchContainer} from '../../../core/definitions/search-container';
import {MetaTypes} from '../../../core/definitions/meta-types';
import {SettingsService} from '../../../core/settings.service';
import {ClientConfig} from "../../../core/definitions/client-config";
import {FeatureFlagsService} from '../../../core/feature-flags.service';

@Component({
    selector: 'app-search-result-view-list-thumbnail',
    templateUrl: './search-result-view-list-thumbnail.component.html',
    styleUrls: ['./search-result-view-list-thumbnail.component.scss'],
    standalone: false
})
export class SearchResultViewListThumbnailComponent implements OnInit {
  // Injections
  private readonly featureFlags: FeatureFlagsService = inject(FeatureFlagsService);
  private readonly searchExecutorService: SearchExecutorService = inject(SearchExecutorService);
  private readonly searchHandler: SearchHandlerService = inject(SearchHandlerService);
  private readonly searchResultSelectionsService: SearchResultSelectionsService = inject(SearchResultSelectionsService);
  private readonly settingsService: SettingsService = inject(SettingsService);

  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;
  @Input() searchContainer: SearchContainer;
  @Input() width: string;

  MetaTypes = MetaTypes;
  ds: VirtualScrollSearchDataSourceList;
  clientConfig: ClientConfig;
  needsDisableRefresh = new Set<string>();

  readonly objectPageV2 = this.featureFlags.getFeatureFlags().experimental.useNewObjectPage;

  ngOnInit() {
    this.clientConfig = this.settingsService.getClientConfig();
    this.ds = new VirtualScrollSearchDataSourceList(this.searchExecutorService, this.searchContainer);
    if (this.searchContainer.virtualScrollViewOffset) {
      setTimeout(() => {
        this.virtualScroll.scrollToOffset(this.searchContainer.virtualScrollViewOffset);
      }, 2000);
    }
  }

  select(obj: SearchObject, event: any) {
    const result = this.searchResultSelectionsService.selectSearchResultItem(obj, this.searchContainer, event.shiftKey);

    // Only prevent default if we have unselected items (hit the limit)
    if (event.shiftKey && result.unselectedItems.length > 0) {
      event.preventDefault();
    }

    // Mark all unselected items for refresh
    result.unselectedItems.forEach(unselectedItem => {
      this.needsDisableRefresh.add(unselectedItem.artifact_id);
    });
  }

  setSearchItemIndex(searchItemIndex: number, event?: Event) {
    if (event && this.shouldIgnoreSearchIndexClick(event)) {
      return;
    }

    if (this.searchContainer && searchItemIndex !== undefined) {
      this.searchContainer.virtualScrollViewOffset = this.virtualScroll.getOffsetToRenderedContentStart();
      this.searchHandler.setSearchItemIndex(this.searchContainer, searchItemIndex);
    }
  }

  private shouldIgnoreSearchIndexClick(event: Event): boolean {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return false;
    }

    return Boolean(target.closest(
      '.overview-field__show-more,' +
      '.object-link-field__show-more,' +
      '.search-col-overview-fields__show-more,' +
      '.mat-mdc-menu-panel,' +
      '.mat-mdc-menu-item,' +
      'button'
    ));
  }

  getRouterLink(art: SearchObject) {
    if (art.$$refData?.routerLink?.join('/')?.includes('/artifact') && this.objectPageV2) {
      return [...art.$$refData.routerLink, art.artifact_id];
    }
    return art.$$refData?.routerLink
  }

  isCheckboxDisabled(art: SearchObject): boolean {
    const itemId = art.artifact_id;
    const isSelected = this.searchContainer.selections.selected[itemId];

    if (this.needsDisableRefresh.has(itemId)) {
      this.needsDisableRefresh.delete(itemId);
      return art.$$used || (!isSelected && this.searchResultSelectionsService.isSelectionLimitReached(this.searchContainer));
    }

    return art.$$used || (!isSelected && this.searchResultSelectionsService.isSelectionLimitReached(this.searchContainer));
  }

  isCheckboxSelected(art: SearchObject): boolean {
    const itemId = art.artifact_id;
    if (this.isCheckboxDisabled(art) && !this.searchContainer.selections.selected[itemId]) {
      return false;
    }
    return this.searchContainer.selections.selected[itemId] || false;
  }
}
